import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { RE2JS } from 're2js';

/**
 * SN-Muster für Scans (bentomax/hawk#611).
 *
 * Manche Teile tragen einen QR-/DataMatrix-Code mit mehr Daten als die
 * aufgedruckte Seriennummer (z. B. Diebold DA53: P/N 01750338654 + S/N
 * 590N011514 in einem Code). Das Muster an der Artikelvorlage ist ein
 * regulärer Ausdruck (ohne Groß-/Kleinschreibung); die Techniker-App wendet
 * ihn auf den Scan an. Die SN ist die benannte Gruppe `sn`, sonst die erste
 * Gruppe, sonst der ganze Treffer; nimmt die Gruppe nicht teil, der ganze
 * Treffer. ^/$ gelten je Zeile. Gleiche Regel wie in der App und in hawk-rails
 * (SerialNumberPattern).
 *
 * Ausgewertet wie in der App mit re2js (RE2): lineare Laufzeit, kein Muster
 * kann das Gerät beim Scannen einfrieren. RE2 kennt keine Lookarounds und
 * Rückverweise – solche Muster sind ungültig.
 */
export const SERIAL_NUMBER_PATTERN_MAX_LENGTH = 255

const FLAGS = RE2JS.CASE_INSENSITIVE | RE2JS.MULTILINE

function compile(pattern: string): RE2JS | null {
  try {
    return RE2JS.compile(pattern, FLAGS)
  } catch {
    return null
  }
}

/** Leere Eingabe ergibt `null`, sonst bleibt das Muster unverändert */
export function normalizeSerialNumberPattern(input: string | null | undefined): string | null {
  if (typeof input !== 'string' || input.trim() === '') return null
  return input
}

/** Fehlermeldung für ein ungültiges Muster, `null` wenn es passt (leer ist gültig) */
export function serialNumberPatternError(pattern: string | null | undefined): string | null {
  const value = normalizeSerialNumberPattern(pattern)
  if (value === null) return null
  if (value.length > SERIAL_NUMBER_PATTERN_MAX_LENGTH) {
    return `Höchstens ${SERIAL_NUMBER_PATTERN_MAX_LENGTH} Zeichen.`
  }
  if (!compile(value)) {
    return 'Kein gültiger regulärer Ausdruck.'
  }
  return null
}

/**
 * Zieht die SN aus einem Scan. `null`, wenn kein Muster gesetzt ist, das
 * Muster ungültig ist oder nicht trifft.
 */
export function extractSerialNumber(scan: string | null | undefined, pattern: string | null | undefined): string | null {
  const value = normalizeSerialNumberPattern(pattern)
  if (value === null || typeof scan !== 'string' || serialNumberPatternError(value)) return null

  const regex = compile(value)
  if (!regex) return null
  const matcher = regex.matcher(scan.trim())
  if (!matcher.find()) return null

  const group = 'sn' in regex.namedGroups() ? matcher.group('sn') : regex.groupCount() > 0 ? matcher.group(1) : null
  const serialNumber = (group ?? matcher.group() ?? '').trim()
  return serialNumber || null
}

/** Formular-Validator: Fehler-Key `serialNumberPattern` mit der Meldung */
export function serialNumberPatternValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const error = serialNumberPatternError(control.value)
    return error ? { serialNumberPattern: error } : null
  }
}
